import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Clock, MessageSquare, Inbox, TrendingUp, Phone, Loader2,
  ArrowRight, MapPin, BellRing, AlertCircle, ShieldCheck, Download,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import apiClient, { API_BASE } from "./api";
import { BRAND } from "../data/mock";

const STATUS_COLOR = {
  new: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  pending: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  contacted: "bg-[#8B73FF]/15 text-[#C9BCFF] border-[#8B73FF]/40",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completed: "bg-white/10 text-white/70 border-white/20",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

function StatCard({ label, value, icon: Icon, accent = "#FC5106", delta }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl border border-white/10 bg-[#121212] p-5 md:p-6 hover:border-white/20 transition-colors"
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs uppercase tracking-wider text-white/50">{label}</p>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}20`, border: `1px solid ${accent}40` }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <p className="font-heading text-3xl md:text-4xl font-bold text-white">{value}</p>
      {delta && <p className="text-xs text-white/50 mt-1">{delta}</p>}
    </div>
  );
}

function whatsappReminderUrl(b) {
  const dateStr = (() => {
    try {
      return format(parseISO(b.date), "PPP");
    } catch {
      return b.date;
    }
  })();
  const lines = [
    `Hi ${b.name}, this is a friendly reminder from BarknFurs Grooming Studio.`,
    "",
    `Your appointment is scheduled for *${dateStr}* at *${b.time_slot}*.`,
    `Service: ${b.service}`,
    `Location: ${b.location === "Home" ? "Home Grooming (Doorstep)" : "At our studio"}`,
    "",
    "Reply YES to confirm or call us if you need to reschedule.",
    `— BarknFurs ${BRAND.phone}`,
  ];
  const phone = (b.phone || "").replace(/\D/g, "");
  const num = phone.length === 10 ? `91${phone}` : phone;
  return `https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState([]);
  const [tomorrow, setTomorrow] = useState([]);
  const [newest, setNewest] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const todayDate = new Date();
      const tomorrowDate = new Date();
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      const fmt = (d) => d.toISOString().slice(0, 10);

      const [statsRes, todayRes, tomorrowRes, newestRes] = await Promise.all([
        apiClient.get("/api/admin/stats"),
        apiClient.get("/api/admin/bookings", {
          params: { date_from: fmt(todayDate), date_to: fmt(todayDate) },
        }),
        apiClient.get("/api/admin/bookings", {
          params: { date_from: fmt(tomorrowDate), date_to: fmt(tomorrowDate) },
        }),
        apiClient.get("/api/admin/bookings", { params: { limit: 5 } }),
      ]);
      setStats(statsRes.data);
      setToday(todayRes.data);
      setTomorrow(tomorrowRes.data);
      setNewest(newestRes.data);
    } catch (e) {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
      </div>
    );
  }

  const t = stats?.totals || {};

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Overview</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            Welcome back!
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Here's what's happening at the studio today.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              const t = toast.loading("Preparing backup…");
              try {
                const token = localStorage.getItem("bf_token");
                const res = await fetch(`${API_BASE}/admin/backup/latest`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Download failed");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `barknfurs-backup-${new Date().toISOString().slice(0, 10)}.zip`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Backup downloaded — keep it safe", { id: t });
              } catch {
                toast.error("Backup failed — try again", { id: t });
              }
            }}
            data-testid="dashboard-download-backup"
            className="btn-secondary !py-2.5 !px-4 !text-sm"
            title="Downloads a ZIP with every booking, enquiry, reminder and task — JSON files you can open in Excel or restore from."
          >
            <Download className="w-4 h-4" /> Download backup
          </button>
          <Link to="/admin/bookings" className="btn-secondary !py-2.5 !px-4 !text-sm">
            All bookings <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Data-safety banner */}
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3" data-testid="data-safety-banner">
        <ShieldCheck className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-emerald-200 font-semibold">Your data is auto-backed up every 24 hours</p>
          <p className="text-emerald-100/70 mt-0.5">
            Every lead, booking, reminder and task is saved permanently. Find all leads on <Link to="/admin/enquiries" className="underline text-emerald-200 hover:text-white">/admin/enquiries</Link>.
            Click "Download backup" anytime for a ZIP copy.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <StatCard label="Today" value={t.today ?? 0} icon={Calendar} accent="#FC5106" />
        <StatCard label="Tomorrow" value={t.tomorrow ?? 0} icon={Clock} accent="#8B73FF" />
        <StatCard label="Reminders due" value={t.reminders_due ?? 0} icon={BellRing} accent={t.reminders_overdue > 0 ? "#FF4757" : "#FC5106"}
          delta={t.reminders_overdue ? `${t.reminders_overdue} overdue` : null} />
        <StatCard label="This week" value={t.this_week ?? 0} icon={TrendingUp} accent="#FC5106" />
        <StatCard label="New leads" value={t.new_bookings ?? 0} icon={Inbox} accent="#FC5106" />
        <StatCard label="New messages" value={t.new_messages ?? 0} icon={MessageSquare} accent="#8B73FF" />
      </div>

      {/* Reminders alert */}
      {(t.reminders_due ?? 0) > 0 && (
        <Link
          to="/admin/reminders"
          data-testid="dashboard-reminders-alert"
          className="block rounded-2xl border border-[#FC5106]/40 bg-gradient-to-r from-[#FC5106]/10 to-transparent p-5 hover:border-[#FC5106]/70 transition"
        >
          <div className="flex items-center gap-4">
            <div className="bg-[#FC5106]/20 border border-[#FC5106]/50 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-[#FC5106]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-heading font-bold text-base md:text-lg">
                {t.reminders_due} customer reminder{t.reminders_due > 1 ? "s" : ""} need follow-up
              </p>
              <p className="text-white/60 text-xs md:text-sm">
                {t.reminders_overdue > 0 && `${t.reminders_overdue} overdue · `}
                Click to view & contact
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#FC5106]" />
          </div>
        </Link>
      )}

      {/* Today + tomorrow */}
      <div className="grid lg:grid-cols-2 gap-5">
        <AppointmentList
          title="Today's appointments"
          subtitle={format(new Date(), "EEEE, MMM d")}
          rows={today}
          empty="No appointments today"
          accent="#FC5106"
          testid="appointments-today"
        />
        <AppointmentList
          title="Tomorrow's appointments"
          subtitle={format(new Date(Date.now() + 86400000), "EEEE, MMM d")}
          rows={tomorrow}
          empty="No appointments tomorrow"
          accent="#8B73FF"
          testid="appointments-tomorrow"
        />
      </div>

      {/* Top services + recent leads */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 lg:col-span-1">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Top services</h3>
          {(stats?.by_service || []).length === 0 ? (
            <p className="text-white/40 text-sm">No data yet.</p>
          ) : (
            <ul className="space-y-3">
              {stats.by_service.map((s) => {
                const max = Math.max(...stats.by_service.map((x) => x.count));
                const pct = Math.round((s.count / max) * 100);
                return (
                  <li key={s.service}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/80 truncate pr-2">{s.service}</span>
                      <span className="text-white/60 font-semibold">{s.count}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#FC5106] to-[#8B73FF]" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-white">Latest bookings</h3>
            <Link to="/admin/bookings" className="text-xs text-[#C9BCFF] hover:underline">
              View all →
            </Link>
          </div>
          {newest.length === 0 ? (
            <p className="text-white/40 text-sm py-6 text-center">No bookings yet.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {newest.map((b) => (
                <li key={b.id} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{b.name}</p>
                    <p className="text-white/50 text-xs truncate">
                      {b.service} · {b.date} {b.time_slot}
                    </p>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                      STATUS_COLOR[b.status] || STATUS_COLOR.new
                    }`}
                  >
                    {b.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function AppointmentList({ title, subtitle, rows, empty, accent, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid={testid}>
      <div className="flex items-end justify-between mb-5">
        <div>
          <h3 className="font-heading text-lg font-bold text-white">{title}</h3>
          <p className="text-white/40 text-xs">{subtitle}</p>
        </div>
        <span
          className="text-xs font-bold px-2 py-1 rounded-full"
          style={{ background: `${accent}20`, color: accent }}
        >
          {rows.length}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-white/40 text-sm py-8 text-center">{empty}</p>
      ) : (
        <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {rows.map((b) => (
            <li
              key={b.id}
              className="rounded-xl border border-white/5 bg-black/30 p-4 hover:border-white/20 transition"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm">{b.name}</p>
                  <p className="text-white/50 text-xs">
                    {b.pet_name ? `${b.pet_name} · ` : ""}
                    {b.pet_type}{b.pet_breed ? ` · ${b.pet_breed}` : ""}
                  </p>
                </div>
                <span className="font-heading font-bold text-white text-sm whitespace-nowrap">
                  {b.time_slot}
                </span>
              </div>
              <p className="text-white/70 text-xs mb-3">{b.service}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={`tel:${b.phone}`}
                  data-testid={`call-${b.id}`}
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 inline-flex items-center gap-1"
                >
                  <Phone className="w-3 h-3" /> Call
                </a>
                <a
                  href={whatsappReminderUrl(b)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`remind-${b.id}`}
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25 inline-flex items-center gap-1"
                >
                  <MessageSquare className="w-3 h-3" /> Send reminder
                </a>
                <span
                  className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                    STATUS_COLOR[b.status] || STATUS_COLOR.new
                  }`}
                >
                  {b.status}
                </span>
                {b.location === "Home" && (
                  <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#8B73FF]/15 border border-[#8B73FF]/40 text-[#C9BCFF] inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Home
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
